import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  catchingTests,
  catchMessage,
  catchRows,
  evidenceLines,
  FIXTURE_PROMPT,
  fixtureCode,
  fixtureLabels,
  fixtureRun,
  FIXTURES,
  hypothesisLabels,
  hypothesisRows,
  HYPOTHESES,
  idLine,
  IT_RAN_TEST,
  LAB_TESTS,
  mistakeChanges,
  mistakeLabels,
  MISTAKES,
  orderLabels,
  overrideCode,
  overrideOutcome,
  overridePath,
  PAGE_SIZE,
  pageRows,
  pagingCondition,
  RUN_ORDERS,
  STARTING_TASKS,
  traceLayer,
  traceMessage,
  traceRun,
  TRACE_STEPS,
  traceStepLabels,
  traceTone,
  type Fixture,
  type Hypothesis,
  type Mistake,
  type RunOrder,
  type Tone,
  type TraceStep,
} from './testing-models';

import './relationship-labs.css';
import './testing-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
  outcome,
  marked,
  current,
}: {
  label: string;
  columns: string[];
  rows: string[][];
  template?: string;
  className?: string;
  /** Column whose cells are coloured as a test outcome. */
  outcome?: number;
  /** Rows to highlight, such as a task the page shows twice. */
  marked?: (row: string[]) => boolean;
  /** The selected row. */
  current?: number;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.map((row, index) => (
          <div
            role="row"
            className="rel-row"
            key={index}
            aria-current={current === index ? 'true' : undefined}
            data-marked={marked?.(row) ? 'true' : undefined}
            style={{ gridTemplateColumns: template }}
          >
            {row.map((cell, column) => (
              <span
                role="cell"
                key={column}
                data-outcome={column === outcome ? cell.split(':')[0] : undefined}
              >
                {cell}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      {label}
    </label>
  );
}
/** A labelled group gives the result its own key in the tutor-visible state, so it never
    replaces the panel or fact that comes first in the lab. */
function Outcome({ tone = 'neutral', children }: { tone?: Tone; children: string }) {
  return (
    <div role="group" aria-label="Result">
      <Result tone={tone}>{children}</Result>
    </div>
  );
}
const SIMULATION = "A simulation of the lesson's tests and data.";

export function TestCatchLab() {
  const [mistake, setMistake] = useState<Mistake>('paging');
  const failing = catchingTests(mistake);
  return (
    <Lab
      title="Which test catches which mistake?"
      eyebrow="Testing model · useful tests"
      className="rel-lab test-lab"
    >
      <Predict>
        Each mistake is made on purpose, one at a time. Predict which of the six tests fails for it,
        and whether the test that only checks GET /tasks answers 200 ever fails.
      </Predict>
      <div className="cb-controls">
        <label>
          Mistake
          <ModelSelect
            aria-label="Mistake"
            value={mistake}
            onValueChange={(value) => setMistake(value as Mistake)}
            options={MISTAKES.map((key) => ({ value: key, label: mistakeLabels[key] }))}
          />
        </label>
        <Reset onClick={() => setMistake('paging')} />
      </div>
      <Facts
        values={[
          ['The mistake', mistakeChanges[mistake]],
          ['Tests that fail', `${failing.length} of ${LAB_TESTS.length}`],
        ]}
      />
      <Table
        label="Tests with this mistake"
        columns={['Test', 'File', 'Result', 'What the test sees']}
        template="minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 2fr)"
        className="test-catch"
        outcome={2}
        rows={catchRows(mistake)}
      />
      <Panel label="The it-ran test">
        <pre aria-label="The it-ran test">{IT_RAN_TEST}</pre>
      </Panel>
      <Outcome>{catchMessage(mistake)}</Outcome>
      <p className="rel-caption">
        {SIMULATION} Caught means the test fails while the mistake is in the code. A useful test
        fails for the mistake you fear; a test that passes with every mistake proves only that the
        code ran.
      </p>
    </Lab>
  );
}

export function FixtureRollbackLab() {
  const [fixture, setFixture] = useState<Fixture>('shared');
  const [order, setOrder] = useState<RunOrder>('ab');
  const [ran, setRan] = useState(false);
  const run = ran ? fixtureRun(fixture, order) : null;
  return (
    <Lab
      title="Do tests depend on each other?"
      eyebrow="Testing model · fixtures"
      className="rel-lab test-lab"
    >
      <Predict>
        Test A creates a task and Test B counts the project&apos;s tasks. Predict each result on a
        shared database in both orders, then with each test in a transaction that is rolled back.
      </Predict>
      <div className="cb-controls">
        <label>
          Fixture
          <ModelSelect
            aria-label="Fixture"
            value={fixture}
            onValueChange={(value) => {
              setFixture(value as Fixture);
              setRan(false);
            }}
            options={FIXTURES.map((key) => ({ value: key, label: fixtureLabels[key] }))}
          />
        </label>
        <label>
          Order
          <ModelSelect
            aria-label="Order"
            value={order}
            onValueChange={(value) => {
              setOrder(value as RunOrder);
              setRan(false);
            }}
            options={RUN_ORDERS.map((key) => ({ value: key, label: orderLabels[key] }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => setRan(true)}>
          Run the tests
        </Button>
        <Reset
          onClick={() => {
            setFixture('shared');
            setOrder('ab');
            setRan(false);
          }}
        />
      </div>
      <Panel label="conftest.py">
        <pre aria-label="conftest.py">{fixtureCode[fixture]}</pre>
      </Panel>
      {run ? (
        <div className="cb-two">
          {run.tests.map((test, index) => (
            <Panel key={test.test} label={`${index + 1}. Test ${test.test}`}>
              <p className="rel-caption">{test.label}</p>
              <Table
                label={`Tasks during Test ${test.test}`}
                columns={['id', 'title', 'Where it came from']}
                template="32px minmax(0, 1fr) minmax(0, 1.4fr)"
                marked={(row) => row[1] === 'Draft'}
                rows={test.sees}
              />
              <Facts values={[[`Test ${test.test}`, test.verdict]]} />
            </Panel>
          ))}
        </div>
      ) : (
        <p className="rel-caption">The tests have not run yet.</p>
      )}
      <Facts
        values={[
          ['Tasks before the run', idLine(STARTING_TASKS)],
          ['Tasks after the run', run ? run.afterLine : '(not run yet)'],
        ]}
      />
      <Outcome tone={run?.tone ?? 'neutral'}>{run ? run.message : FIXTURE_PROMPT}</Outcome>
      <p className="rel-caption">
        {SIMULATION} Every run starts from the project&apos;s two starting tasks. In the workshop
        each test also creates its own users, project and tasks with make, on the same connection,
        so the rollback removes them too.
      </p>
    </Lab>
  );
}

export function OverrideFlowLab() {
  const [connection, setConnection] = useState(false);
  const [user, setUser] = useState(false);
  const path = overridePath(connection, user);
  const result = overrideOutcome(connection, user);
  return (
    <Lab
      title="What does the route receive in a test?"
      eyebrow="Testing model · dependency overrides"
      className="rel-lab test-lab"
    >
      <Predict>
        The test creates a task in Apollo as Ravi. Predict what it needs before the request and what
        it leaves in the database with no overrides, then with each override switched on.
      </Predict>
      <div className="cb-controls">
        <Toggle
          label="Override the connection"
          checked={connection}
          onChange={() => setConnection((current) => !current)}
        />
        <Toggle
          label="Override the signed-in user"
          checked={user}
          onChange={() => setUser((current) => !current)}
        />
        <Reset
          onClick={() => {
            setConnection(false);
            setUser(false);
          }}
        />
      </div>
      <Panel label="The test">
        <pre aria-label="The test">{overrideCode(connection, user)}</pre>
      </Panel>
      <ol className="test-path" aria-label="Request path">
        {path.map((step) => (
          <li key={step.part} data-overridden={step.overridden ? 'true' : undefined}>
            <strong>{step.part}</strong>
            <small>{step.tag}</small>
            <p>{step.detail}</p>
          </li>
        ))}
      </ol>
      <Facts
        values={[
          ['The route receives', result.receives],
          ['Needs a real login', result.needsLogin],
          ['After the test', result.afterTest],
          ['Response', result.response],
        ]}
      />
      <Outcome tone={result.tone}>{result.message}</Outcome>
      <p className="rel-caption">
        {SIMULATION} client_as sets both overrides for the user it is given and clears
        app.dependency_overrides after the test, so one test&apos;s overrides never reach the next.
      </p>
    </Lab>
  );
}

export function DebugTraceLab() {
  const [step, setStep] = useState<TraceStep | null>(null);
  const [seen, setSeen] = useState<TraceStep[]>([]);
  const [hypothesis, setHypothesis] = useState<Hypothesis>('page-twice');
  const [fixed, setFixed] = useState(false);
  const run = traceRun(fixed);
  const show = (next: TraceStep) => {
    setStep(next);
    setSeen((current) => (current.includes(next) ? current : [...current, next]));
  };
  return (
    <Lab
      title="Where is the duplicate coming from?"
      eyebrow="Debugging model · across layers"
      className="rel-lab test-lab"
    >
      <Predict>
        Maya pressed Load more tasks on Apollo and saw Print the badges twice. Pick the hypothesis
        you suspect, predict which step would rule it out, then follow the evidence layer by layer.
      </Predict>
      <div className="cb-controls">
        <label>
          Hypothesis
          <ModelSelect
            aria-label="Hypothesis"
            value={hypothesis}
            onValueChange={(value) => setHypothesis(value as Hypothesis)}
            options={HYPOTHESES.map((key) => ({ value: key, label: hypothesisLabels[key] }))}
          />
        </label>
      </div>
      <div className="cb-tabs" role="group" aria-label="Evidence steps">
        {TRACE_STEPS.map((entry) => (
          <button
            key={entry}
            type="button"
            aria-pressed={step === entry}
            onClick={() => show(entry)}
          >
            {traceStepLabels[entry]}
          </button>
        ))}
      </div>
      <div className="cb-controls">
        <Toggle
          label="Apply the fix"
          checked={fixed}
          disabled={!seen.includes('sql')}
          onChange={() => setFixed((current) => !current)}
        />
        <Reset
          onClick={() => {
            setStep(null);
            setSeen([]);
            setHypothesis('page-twice');
            setFixed(false);
          }}
        />
      </div>
      <Panel label={step ? traceStepLabels[step] : 'Evidence'}>
        {step === null ? (
          <p className="rel-caption">Choose a step to see its evidence.</p>
        ) : step === 'page' ? (
          <Table
            label="Maya's page"
            columns={['#', 'id', 'title', 'Loaded by']}
            template="28px 36px minmax(0, 1.6fr) minmax(0, 1fr)"
            marked={(row) => run.repeated.includes(Number(row[1]))}
            rows={pageRows(fixed)}
          />
        ) : (
          <pre aria-label={traceStepLabels[step]}>{evidenceLines(step, fixed).join('\n')}</pre>
        )}
      </Panel>
      <Table
        label="Hypotheses"
        columns={['Hypothesis', 'Tested by', 'Status']}
        template="minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)"
        className="test-hypotheses"
        outcome={2}
        current={HYPOTHESES.indexOf(hypothesis)}
        rows={hypothesisRows(seen)}
      />
      <Facts
        values={[
          [
            'Steps taken',
            seen.length ? seen.map((entry) => traceStepLabels[entry]).join(' → ') : '(none yet)',
          ],
          ['Where the duplicate starts', traceLayer(seen)],
          ['Paging condition', pagingCondition(fixed)],
          ['Page 2', run.page2.tasks.map((task) => task.id).join(', ')],
        ]}
      />
      <Outcome tone={traceTone(seen, fixed)}>{traceMessage(step, seen, hypothesis, fixed)}</Outcome>
      <p className="rel-caption">
        A simulation of the starter&apos;s paging on the lesson&apos;s data. The page asks for{' '}
        {PAGE_SIZE} tasks at a time, and Apollo has tasks 9, 10 and 12 to 16 (11 is Jo&apos;s
        personal task). Apply the fix becomes available once the SQL step has found the cause.
      </p>
    </Lab>
  );
}
